// Lightweight admin i18n. Strategy: the FRENCH string IS the key, so French is
// the identity (zero risk if a string is ever missed) and English is a lookup
// with graceful fallback to French. One context, persisted to localStorage.
//
//   const { t, lang, setLang } = useT();
//   t('Enregistrer')                       → "Enregistrer" (fr) / "Save" (en)
//   t('{n} commandes', { n: 5 })           → interpolates {n}
//
// English translations live in per-page fragments under ./i18n/en/* and are
// merged in ./i18n/en (so page work never collides on one big file).
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { EN } from './i18n/en';

export type Lang = 'fr' | 'en';
const LS_KEY = 'storiz_admin_lang';

interface LangCtx { lang: Lang; setLang: (l: Lang) => void }
const Ctx = createContext<LangCtx>({ lang: 'fr', setLang: () => {} });

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    try { const v = localStorage.getItem(LS_KEY); if (v === 'en' || v === 'fr') return v; } catch { /* ignore */ }
    return 'fr';
  });
  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try { localStorage.setItem(LS_KEY, l); } catch { /* ignore */ }
    if (typeof document !== 'undefined') document.documentElement.lang = l;
  }, []);
  return <Ctx.Provider value={{ lang, setLang }}>{children}</Ctx.Provider>;
}

export type TFunc = (fr: string, vars?: Record<string, string | number>) => string;

export function useT(): { t: TFunc; lang: Lang; setLang: (l: Lang) => void } {
  const { lang, setLang } = useContext(Ctx);
  const t = useCallback<TFunc>((fr, vars) => {
    let s = lang === 'en' ? (EN[fr] ?? fr) : fr;
    if (vars) for (const k of Object.keys(vars)) s = s.split(`{${k}}`).join(String(vars[k]));
    return s;
  }, [lang]);
  return { t, lang, setLang };
}
